import { CSSProperties } from 'react'

import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { UserDocument } from '@/types/unlock'

export interface FileManagerFile extends UserDocument {
  fileName?: string
  filePath?: string
  readonly?: boolean
  modifiedAt?: Date
  downloadPath?: string
}

export const MAX_FILE_SIZE = 200 * 1024 * 1024 // 200 MB

export const SUPPORTED_FORMATS = [
  'application/json',
  'application/msword',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/xml',
  'audio/mpeg',
  'image/gif',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'text/csv',
  'text/plain',
  'text/xml',
  'video/quicktime',
  'video/x-ms-wmv',
  'video/x-msvideo',
  'video/mp4',
]

export const ROOT_FOLDER = 'Deal Files'
export const SERVICING_FOLDER = 'Servicing'
export const ROOT_FOLDERS = [ROOT_FOLDER, SERVICING_FOLDER]
export const HEO_DOCS_FOLDER = 'HEO Docs'

type ModalKeys =
  | 'RenameV2'
  | 'Rename'
  | 'Move'
  | 'Delete'
  | 'Upload'
  | 'Docusign'
  | 'DocusignV2'
  | 'RenamePreset'
  | 'SaveAsPreset'

export const MODALS: Record<ModalKeys, ModalType> = {
  Rename: {
    type: 'file-edit',
    title: 'Rename',
    css: { width: 500 },
    mode: 'rename',
  },
  RenameV2: {
    type: 'file-rename',
    title: 'Rename File',
    css: { width: 1280 },
    mode: 'rename',
  },
  Move: {
    type: 'file-edit',
    title: 'Move File To',
    css: { width: 500 },
    mode: 'move',
  },
  Delete: {
    type: 'file-delete',
    title: 'Delete File',
    css: { width: 500 },
  },
  Upload: {
    type: 'file-upload',
    title: 'Upload Files',
    css: { width: 1280 },
  },
  Docusign: {
    type: 'docusign',
    title: 'Send to Docusign',
    css: { width: 800 },
  },
  DocusignV2: {
    type: 'docusign-v2',
    title: 'Send a document to be Docusigned',
    css: { width: 1000 },
  },
  RenamePreset: {
    type: 'preset-rename',
    title: 'Rename View',
    css: { width: 400 },
  },
  SaveAsPreset: {
    type: 'preset-save-as',
    title: 'Save As Preset',
    css: { width: 400 },
  },
} as const

export const DEFAULT_HEADINGS = [
  { key: 'fileName', name: 'Name' },
  { key: 'size', name: 'Size' },
  { key: 'modifiedAt', name: 'Modified' },
]

type OrderBy = { label?: string; order?: 'desc' | 'asc' }

type ModalType = {
  type: string | null
  title: string
  css?: CSSProperties
  mode?: string
  file?: FileManagerFile
}

type StateType = {
  selectedFolder: string
  selectedParentFolder: string
  selectedFiles: FileManagerFile[]
  search: string
  orderBy: OrderBy
  activeModal: ModalType
  searchAllFolders: boolean
  isMaxViewerFileListOpen: boolean
  isMetadataViewerOpen: boolean
  adminSelectedAppId?: string
  setFileNotifications?: Array<string>
  isICSDocGenerated: boolean
}

const initialState: StateType = {
  selectedFolder: ROOT_FOLDERS[0],
  selectedParentFolder: '',
  selectedFiles: [],
  search: '',
  orderBy: { label: 'fileName', order: 'desc' },
  activeModal: null,
  searchAllFolders: false,
  isMaxViewerFileListOpen: false,
  isMetadataViewerOpen: false,
  setFileNotifications: [],
  isICSDocGenerated: false,
}

const fileManagerSlice = createSlice({
  name: 'fileManager',
  initialState,
  reducers: {
    setSelectedFolder: (state, action: PayloadAction<string>) => {
      state.selectedFolder = action.payload
    },
    setSelectedFolderParent: (state, action: PayloadAction<string>) => {
      state.selectedParentFolder = action.payload
    },
    clearFiles: (state) => {
      state.selectedFiles = []
    },
    selectFiles: (state, action: PayloadAction<FileManagerFile[]>) => {
      const files = action.payload

      files.forEach((file) => {
        const isSelected = !!state.selectedFiles.find((it) => it.uuid === file.uuid)
        if (!isSelected) {
          state.selectedFiles.push(file)
        }
      })
    },
    deselectFiles: (state, action: PayloadAction<FileManagerFile[]>) => {
      const files = action.payload

      const deselectedUuids = files.map((it) => it.uuid)

      state.selectedFiles = state.selectedFiles.filter(
        (selected) => !deselectedUuids.includes(selected.uuid)
      )
    },
    toggleFile: (state, action: PayloadAction<FileManagerFile>) => {
      const file = action.payload

      const isSelected = !!state.selectedFiles.find((it) => it.uuid === file.uuid)

      if (isSelected) {
        state.selectedFiles = state.selectedFiles.filter(
          (selectedFile) => selectedFile.uuid !== file.uuid
        )
      } else {
        state.selectedFiles.push(file)
      }
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload
    },
    setOrderBy: (state, action: PayloadAction<OrderBy>) => {
      state.orderBy = action.payload
    },
    setActiveModal: (state, action: PayloadAction<ModalType>) => {
      state.activeModal = action.payload
    },
    setSearchAllFolders: (state, action: PayloadAction<boolean>) => {
      state.searchAllFolders = action.payload
    },
    setAdminSelectedAppId: (state, action: PayloadAction<string>) => {
      state.adminSelectedAppId = action.payload
    },
    setIsMaxViewerFileListOpen: (state, action: PayloadAction<boolean>) => {
      state.isMaxViewerFileListOpen = action.payload
    },
    setIsMetadataViewerOpen: (state, action: PayloadAction<boolean>) => {
      state.isMetadataViewerOpen = action.payload
    },
    setFileNotifications: (state, action: PayloadAction<Array<string>>) => {
      state.setFileNotifications = action.payload
    },
    setIsICSDocGenerated: (state, action: PayloadAction<boolean>) => {
      state.isICSDocGenerated = action.payload
    },
  },
})

export const {
  setSelectedFolder,
  setSelectedFolderParent,
  toggleFile,
  clearFiles,
  setSearch,
  setOrderBy,
  setActiveModal,
  deselectFiles,
  selectFiles,
  setSearchAllFolders,
  setAdminSelectedAppId,
  setIsMaxViewerFileListOpen,
  setIsMetadataViewerOpen,
  setFileNotifications,
  setIsICSDocGenerated,
} = fileManagerSlice.actions

export const fileManagerReducer = fileManagerSlice.reducer
